import React, { useEffect, useState } from 'react';
import { create } from 'zustand';

interface Notification {
 id: string;
 type: 'mail' | 'invitation' | 'family_invitation' | 'info' | 'success' | 'error' | 'chat';
 title: string;
 message: string;
 timestamp: number;
 link?: string;
 onClick?: () => void;
}

interface NotificationStore {
 notifications: Notification[];
 addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
 removeNotification: (id: string) => void;
}

export const useNotificationStore = create<NotificationStore>((set) => ({
 notifications: [],
 
 addNotification: (notification) => {
 const newNotification: Notification = {
 ...notification,
 id: `${Date.now()}-${Math.random()}`,
 timestamp: Date.now()
 };
 
 set((state) => ({
 notifications: [...state.notifications, newNotification]
 }));
 
 // Auto remove after 5 seconds
 setTimeout(() => {
 set((state) => ({
 notifications: state.notifications.filter(n => n.id !== newNotification.id)
 }));
 }, 5000);
 },
 
 removeNotification: (id) => {
 set((state) => ({
 notifications: state.notifications.filter(n => n.id !== id)
 }));
 }
}));

const NotificationToast: React.FC = () => {
 const { notifications, removeNotification } = useNotificationStore();
 
 const getIcon = (type: Notification['type']) => {
 switch (type) {
 case 'mail':
 return (
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
 d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
 </svg>
 );
 case 'invitation':
 case 'family_invitation':
 return (
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
 d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
 </svg>
 );
 case 'chat':
 return (
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
 d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
 </svg>
 );
 case 'success':
 return (
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
 d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
 </svg>
 );
 case 'error':
 return (
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
 d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
 </svg>
 );
 default:
 return (
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
 d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
 </svg>
 );
 }
 };
 
 const getBackgroundColor = (type: Notification['type']) => {
 switch (type) {
 case 'mail':
 return 'bg-blue-500';
 case 'invitation':
 case 'family_invitation':
 return 'bg-purple-500';
 case 'chat':
 return 'bg-indigo-500';
 case 'success':
 return 'bg-green-500';
 case 'error':
 return 'bg-red-500';
 default:
 return 'bg-gray-500';
 }
 };
 
 const handleClick = (notification: Notification) => {
 if (notification.onClick) {
 notification.onClick();
 } else if (notification.link) {
 window.location.href = notification.link;
 }
 removeNotification(notification.id);
 };
 
 return (
 <div className="fixed top-4 right-4 z-50 space-y-2">
 {notifications.map((notification) => (
 <div
 key={notification.id}
 onClick={() => handleClick(notification)}
 className={`${getBackgroundColor(notification.type)} text-white p-4 shadow-lg 
 max-w-sm cursor-pointer transform transition-all duration-300 hover:scale-105
 animate-slide-in-right`}
 >
 <div className="flex items-start gap-3">
 <div className="flex-shrink-0">
 {getIcon(notification.type)}
 </div>
 <div className="flex-1">
 <h4 className="font-semibold">{notification.title}</h4>
 <p className="text-sm mt-1 opacity-90">{notification.message}</p>
 </div>
 <button
 onClick={(e) => {
 e.stopPropagation();
 removeNotification(notification.id);
 }}
 className="flex-shrink-0 ml-2 hover:opacity-80"
 >
 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 </button>
 </div>
 </div>
 ))}
 </div>
 );
};

export default NotificationToast;